/* eslint-disable max-lines */
import React from 'react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { useIntl } from 'react-intl';
import { BrowserRouter as Router, generatePath, Redirect, Route, Switch } from 'react-router-dom';

import { ScrollToTop } from '../../components/ScrollToTop/ScrollToTop';
import { routes } from '../../constants/routing';
import { featureStateHooks } from '../../hooks/featureStateHooks/featureStateHooks';
import { languageHooks } from '../../hooks/languageHooks/languageHooks';
import { AccountRedirectContainer } from '../AccountRedirectContainer/AccountRedirectContainer';
import { AppContainer } from '../AppContainer/AppContainer';
import { AppDataContainer } from '../AppDataContainer/AppDataContainer';
import { ErrorModalConatiner } from '../ErrorModalConatiner/ErrorModalConatiner';
import {
  FeatureToggleContainerStoryblok,
} from '../FeatureToggleContainerStoryblok/FeatureToggleContainerStoryblok';
import { LocaleDetectContainer } from '../LocaleDetectContainer/LocaleDetectContainer';
import { MetaNotFoundContainer } from '../MetaNotFoundContainer/MetaNotFoundContainer';
import { PageViewEventContainer } from '../PageViewEventContainer/PageViewEventContainer';
import { PrerenderBasketContainer } from '../PrerenderBasketContainer/PrerenderBasketContainer';
import { PrerenderContainer } from '../PrerenderContainer/PrerenderContainer';
import { ResponsiveStateContainer } from '../ResponsiveStateContainer/ResponsiveStateContainer';

const HomePageV2 = React.lazy(
  () => import('../../components/HomePageV2/HomePageV2'),
);
const ProductsOverviewPage =
  React.lazy(() => import('../../components/ProductsOverviewPage/ProductsOverviewPage'));
const ProductDetailsPage =
  React.lazy(() => import('../../components/ProductDetailsPage/ProductDetailsPage'));
const BasketPageContainer =
  React.lazy(() => import('../../containers/BasketPageContainer/BasketPageContainer'));
const CheckoutPage = React.lazy(() => import('../../components/CheckoutPage/CheckoutPage'));
const ConfirmationPage =
  React.lazy(() => import('../../components/ConfirmationPage/ConfirmationPage'));
const GivingMomentsOverviewPage = React.lazy(
  () => import('../../containers/GivingMomentsOverviewPage/GivingMomentsOverviewPage'),
);
const GivingMomentsPage =
  React.lazy(() => import('../../components/GivingMomentsPage/GivingMomentsPage'));
const BusinessPageContainer =
  React.lazy(() => import('../../containers/BusinessPageContainer/BusinessPageContainer'));
const TermsAndConditionsPage =
  React.lazy(() => import('../../components/TermsAndConditionsPage/TermsAndConditionsPage'));
const CustomerSupportPageContainer =
  React.lazy(() => import('../CustomerSupportPageContainer/CustomerSupportPageContainer'));
const ContactPage = React.lazy(() => import('../../components/ContactPage/ContactPage'));
const BlogOverviewPage =
  React.lazy(() => import('../../components/BlogOverviewPage/BlogOverviewPage'));
const BlogArticlePage =
  React.lazy(() => import('../../components/BlogArticlePage/BlogArticlePage'));
const NotFoundPage =
  React.lazy(() => import('../../components/NotFoundPage/NotFoundPage'));
const CookieStatementPage =
  React.lazy(() => import('../../components/CookieStatementPage/CookieStatementPage'));
const CategoryPage =
  React.lazy(() => import('../../components/CategoryPage/CategoryPage'));
const RedeemRoutingContainer =
  React.lazy(() => import('../RedeemRoutingContainer/RedeemRoutingContainer'));
const DoublerShopPageContainer =
  React.lazy(() => import('../../containers/DoublerShopPageContainer/DoublerShopPageContainer'));
const DoublerPage = React.lazy(() => import('../../components/DoublerPage/DoublerPage'));
const AccountIdentifyContainer =
  React.lazy(() => import('../AccountIdentifyContainer/AccountIdentifyContainer'));
const AccountOverviewPageWithEntity = React.lazy(() => import(
  '../../components/AccountOverviewPageWithEntity/AccountOverviewPageWithEntity'
));
const AccountRegisterPageContainer =
  React.lazy(() => import('../AccountRegisterPageContainer/AccountRegisterPageContainer'));
const AccountLoginPageContainer =
  React.lazy(() => import('../AccountLoginPageContainer/AccountLoginPageContainer'));
const AccountForgotPasswordPageContainer = React.lazy(
  () => import('../AccountForgotPasswordPageContainer/AccountForgotPasswordPageContainer'),
);
const LocationsOverviewPage =
  React.lazy(() => import('../LocationsOverviewPage/LocationsOverviewPage'));
const LocationDetailsPageWithEntity = React.lazy(() => import(
  '../../components/LocationDetailsPageWithEntity/LocationDetailsPageWithEntity'
));
const AccountForgotPasswordConfirmPageContainer = React.lazy(() =>
  import('../AccountForgotPasswordConfirmPageContainer/AccountForgotPasswordConfirmPageContainer'));
const AccountResetPasswordConfirmPage = React.lazy(() =>
  import('../../components/AccountResetPasswordConfirmPage/AccountResetPasswordConfirmPage'));
const AccountDetailsPageContainer = React.lazy(() =>
  import('../../containers/AccountDetailsPageContainer/AccountDetailsPageContainer'));
const AccountResetPasswordPageContainer = React.lazy(() =>
  import('../AccountResetPasswordPageContainer/AccountResetPasswordPageContainer'));
const AccountRegisterConfirmPage = React.lazy(() =>
  import('../../components/AccountRegisterConfirmPage/AccountRegisterConfirmPage'));
const AccountOrdersPageContainer = React.lazy(() =>
  import('../../containers/AccountOrdersPageContainer/AccountOrdersPageContainer'));
const ActivationPageContainer = React.lazy(() =>
  import('../../containers/ActivationPageContainer/ActivationPageContainer'));
const ActivationConfirmationContainer = React.lazy(() =>
  import('../../containers/ActivationConfirmationContainer/ActivationConfirmationContainer'));
const ReorderContainer = React.lazy(() =>
  import('../../containers/ReorderContainer/ReorderContainer'));
const LandingPage = React.lazy(() =>
  import('../../components/LandingPage/LandingPage'));
const AdyenProcessingContainer = React.lazy(() =>
  import('../../containers/AdyenProcessingContainer/AdyenProcessingContainer'));

export const RoutingContainer = React.memo(() => {
  const intl = useIntl();
  const languageParamRegex = languageHooks.useGetLanguageParamRegex();

  const {
    isOrderParam,
    isLangSwitcherFixOn,
  } = featureStateHooks.useFeatureToggles();

  return (
    <>
      <FeatureToggleContainerStoryblok forceLoad />
      <MetaNotFoundContainer />
      <Router>
        <AppContainer>
          <ResponsiveStateContainer />
          {!isLangSwitcherFixOn && <AppDataContainer />}
          <ErrorModalConatiner />
          <PageViewEventContainer />
          <LocaleDetectContainer />
          <React.Suspense fallback=''>
            <Switch>
              <Route
                exact
                strict
                path={['/:url*']}
                render={(props) => (
                  <Redirect to={`${props.location.pathname}/${props.location.search}`} />
                )}
              />
              <Route exact path={intl.formatMessage(routes.homePageRoute, {
                languageParamRegex,
              })}>
                {isLangSwitcherFixOn && <AppDataContainer />}
                <ScrollToTop />
                <HomePageV2 />
              </Route>

              <Route exact path={intl.formatMessage(routes.locationsOverviewPageRoute, {
                languageParamRegex,
              })}>
                {isLangSwitcherFixOn && <AppDataContainer />}
                <ScrollToTop />
                <LocationsOverviewPage />
              </Route>

              <Route exact path={intl.formatMessage(routes.locationDetailsPageRoute, {
                languageParamRegex,
              })}>
                {isLangSwitcherFixOn && <AppDataContainer />}
                <ScrollToTop />
                <LocationDetailsPageWithEntity />
              </Route>

              <Route exact path={intl.formatMessage(routes.adyenProcessingPageRoute, {
                languageParamRegex,
              })}>
                {isLangSwitcherFixOn && <AppDataContainer />}
                <ScrollToTop />
                <AdyenProcessingContainer />
              </Route>

              <Route exact path={intl.formatMessage(routes.activationConfirmationPageRoute, {
                languageParamRegex,
              })}>
                {isLangSwitcherFixOn && <AppDataContainer />}
                <ScrollToTop />
                <ActivationConfirmationContainer />
              </Route>

              <Route path={intl.formatMessage(routes.activationPageRoute, {
                languageParamRegex,
              })} exact>
                {isLangSwitcherFixOn && <AppDataContainer />}
                <ScrollToTop />
                <PrerenderContainer storeData entity />
                <ActivationPageContainer />
              </Route>

              <Route exact path={intl.formatMessage(routes.popRoute, {
                languageParamRegex,
              })}>
                {isLangSwitcherFixOn && <AppDataContainer />}
                <ScrollToTop />
                <PrerenderContainer storeData entity products blocksQueue />
                <ProductsOverviewPage />
              </Route>

              <Route exact path={intl.formatMessage(routes.pdpRoute, {
                languageParamRegex,
              })}>
                {isLangSwitcherFixOn && <AppDataContainer />}
                <ScrollToTop />
                <PrerenderContainer storeData entity />
                <ProductDetailsPage />
              </Route>

              <Route exact path={intl.formatMessage(routes.basketPageRoute, {
                languageParamRegex,
              })}>
                {isLangSwitcherFixOn && <AppDataContainer />}
                <ScrollToTop />
                <PrerenderBasketContainer />
                <GoogleReCaptchaProvider
                  reCaptchaKey={CONFIG.reCaptchaKey}
                  scriptProps={{ defer: true }}
                >
                  <BasketPageContainer />
                </GoogleReCaptchaProvider>
              </Route>

              <Route exact path={intl.formatMessage(routes.checkoutPageRoute, {
                languageParamRegex,
              })}>
                {isLangSwitcherFixOn && <AppDataContainer />}
                <ScrollToTop />
                <CheckoutPage />
              </Route>

              <Route exact path={isOrderParam
                ? intl.formatMessage(routes.confirmationPageRouteV2, { languageParamRegex })
                : intl.formatMessage(routes.confirmationPageRoute, { languageParamRegex })}
              >
                {isLangSwitcherFixOn && <AppDataContainer />}
                <ScrollToTop />
                <ConfirmationPage isOrderParam={isOrderParam}/>
              </Route>

              <Route path={intl.formatMessage(routes.redeemPageRoute, {
                languageParamRegex,
              })}>
                {isLangSwitcherFixOn && <AppDataContainer />}
                <ScrollToTop />
                <RedeemRoutingContainer />
              </Route>

              <Route exact path={intl.formatMessage(routes.givingMomentsPopRoute, {
                languageParamRegex,
              })}>
                {isLangSwitcherFixOn && <AppDataContainer />}
                <ScrollToTop />
                <PrerenderContainer storeData entity />
                <GivingMomentsOverviewPage />
              </Route>

              <Route exact path={intl.formatMessage(routes.givingMomentsPdpRoute, {
                languageParamRegex,
              })}>
                {isLangSwitcherFixOn && <AppDataContainer />}
                <ScrollToTop />
                <PrerenderContainer storeData entity />
                <GivingMomentsPage />
              </Route>

              <Route exact path={intl.formatMessage(routes.businessPageRoute, {
                languageParamRegex,
              })}>
                {isLangSwitcherFixOn && <AppDataContainer />}
                <ScrollToTop />
                <PrerenderContainer storeData entity />
                <GoogleReCaptchaProvider
                  reCaptchaKey={CONFIG.reCaptchaKey}
                  scriptProps={{ defer: true }}
                >
                  <BusinessPageContainer />
                </GoogleReCaptchaProvider>
              </Route>

              <Route exact path={intl.formatMessage(routes.termsAndConditionsRoute, {
                languageParamRegex,
              })}>
                {isLangSwitcherFixOn && <AppDataContainer />}
                <ScrollToTop />
                <PrerenderContainer storeData entity />
                <TermsAndConditionsPage />
              </Route>

              <Route exact path={intl.formatMessage(routes.privacyPageRoute, {
                languageParamRegex,
              })}>
                {isLangSwitcherFixOn && <AppDataContainer />}
                <ScrollToTop />
                <PrerenderContainer storeData entity />
                <TermsAndConditionsPage />
              </Route>

              <Route exact path={intl.formatMessage(routes.deliveryPageRoute, {
                languageParamRegex,
              })}>
                {isLangSwitcherFixOn && <AppDataContainer />}
                <ScrollToTop />
                <PrerenderContainer storeData entity />
                <TermsAndConditionsPage />
              </Route>

              <Route exact path={intl.formatMessage(routes.cancelationPolicyPageRoute, {
                languageParamRegex,
              })}>
                {isLangSwitcherFixOn && <AppDataContainer />}
                <ScrollToTop />
                <PrerenderContainer storeData entity />
                <TermsAndConditionsPage />
              </Route>

              <Route exact path={intl.formatMessage(routes.helpdeskRoute, {
                languageParamRegex,
              })}>
                {isLangSwitcherFixOn && <AppDataContainer />}
                <ScrollToTop />
                <PrerenderContainer storeData csIframe />
                <CustomerSupportPageContainer />
              </Route>

              <Route exact path={intl.formatMessage(routes.contactPageRoute, {
                languageParamRegex,
              })}>
                {isLangSwitcherFixOn && <AppDataContainer />}
                <ScrollToTop />
                <PrerenderContainer storeData entity />
                <ContactPage />
              </Route>

              <Route exact path={intl.formatMessage(routes.blogPopRoute, {
                languageParamRegex,
              })}>
                {isLangSwitcherFixOn && <AppDataContainer />}
                <ScrollToTop />
                <PrerenderContainer storeData entity />
                <BlogOverviewPage />
              </Route>

              <Route exact path={intl.formatMessage(routes.blogPdpRoute, {
                languageParamRegex,
              })}>
                {isLangSwitcherFixOn && <AppDataContainer />}
                <ScrollToTop />
                <PrerenderContainer storeData entity />
                <BlogArticlePage />
              </Route>

              <Route exact path={intl.formatMessage(routes.cookiesPageRoute, {
                languageParamRegex,
              })}>
                {isLangSwitcherFixOn && <AppDataContainer />}
                <ScrollToTop />
                <PrerenderContainer storeData timeoutMs={10000} />
                <CookieStatementPage />
              </Route>

              <Route exact path={intl.formatMessage(routes.allCategoriesPageRoute, {
                languageParamRegex,
              })}>
                {isLangSwitcherFixOn && <AppDataContainer />}
                <ScrollToTop />
                <Redirect to={generatePath(intl.formatMessage(routes.popRoute))} />
              </Route>

              <Route exact path={intl.formatMessage(routes.categoryPageRoute, {
                languageParamRegex,
              })}>
                {isLangSwitcherFixOn && <AppDataContainer />}
                <ScrollToTop />
                <PrerenderContainer storeData entity products blocksQueue />
                <CategoryPage />
              </Route>

              <Route exact path={intl.formatMessage(routes.doublerConfirmationPageRoute, {
                languageParamRegex,
              })}>
                {isLangSwitcherFixOn && <AppDataContainer />}
                <ScrollToTop />
                <PrerenderContainer entity storeData />
                <DoublerShopPageContainer />
              </Route>

              <Route exact path={intl.formatMessage(routes.doublerHomePageRoute, {
                languageParamRegex,
              })}>
                {isLangSwitcherFixOn && <AppDataContainer />}
                <ScrollToTop />
                <PrerenderContainer storeData />
                <DoublerPage />
              </Route>

              <Route exact path={intl.formatMessage(routes.accountIdentifyPageRoute, {
                languageParamRegex,
              })}>
                {isLangSwitcherFixOn && <AppDataContainer />}
                <ScrollToTop />
                <GoogleReCaptchaProvider
                  reCaptchaKey={CONFIG.reCaptchaKey}
                  scriptProps={{ defer: true }}
                >
                  <AccountRedirectContainer>
                    <AccountIdentifyContainer />
                  </AccountRedirectContainer>
                </GoogleReCaptchaProvider>
              </Route>

              <Route exact path={intl.formatMessage(routes.loginPageRoute, {
                languageParamRegex,
              })}>
                {isLangSwitcherFixOn && <AppDataContainer />}
                <ScrollToTop />
                <GoogleReCaptchaProvider
                  reCaptchaKey={CONFIG.reCaptchaKey}
                  scriptProps={{ defer: true }}
                >
                  <AccountRedirectContainer>
                    <AccountLoginPageContainer />
                  </AccountRedirectContainer>
                </GoogleReCaptchaProvider>
              </Route>

              <Route exact path={intl.formatMessage(routes.accountRegisterPageRoute, {
                languageParamRegex,
              })}>
                {isLangSwitcherFixOn && <AppDataContainer />}
                <ScrollToTop />
                <GoogleReCaptchaProvider
                  reCaptchaKey={CONFIG.reCaptchaKey}
                  scriptProps={{ defer: true }}
                >
                  <AccountRedirectContainer>
                    <AccountRegisterPageContainer />
                  </AccountRedirectContainer>
                </GoogleReCaptchaProvider>
              </Route>

              <Route exact path={intl.formatMessage(routes.accountRegisterSuccessPageRoute, {
                languageParamRegex,
              })}>
                {isLangSwitcherFixOn && <AppDataContainer />}
                <ScrollToTop />
                <AccountRegisterConfirmPage />
              </Route>

              <Route exact path={intl.formatMessage(routes.forgotPasswordPageRoute, {
                languageParamRegex,
              })}>
                {isLangSwitcherFixOn && <AppDataContainer />}
                <ScrollToTop />
                <GoogleReCaptchaProvider
                  reCaptchaKey={CONFIG.reCaptchaKey}
                  scriptProps={{ defer: true }}
                >
                  <AccountRedirectContainer>
                    <AccountForgotPasswordPageContainer />
                  </AccountRedirectContainer>
                </GoogleReCaptchaProvider>
              </Route>

              <Route exact path={intl.formatMessage(routes.forgotPasswordConfirmPageRoute, {
                languageParamRegex,
              })}>
                {isLangSwitcherFixOn && <AppDataContainer />}
                <ScrollToTop />
                <AccountForgotPasswordConfirmPageContainer />
              </Route>

              <Route exact path={intl.formatMessage(routes.newPasswordPageRoute, {
                languageParamRegex,
              })}>
                {isLangSwitcherFixOn && <AppDataContainer />}
                <ScrollToTop />
                <AccountRedirectContainer>
                  <AccountResetPasswordPageContainer />
                </AccountRedirectContainer>
              </Route>

              <Route exact path={intl.formatMessage(routes.newPasswordConfirmationPageRoute, {
                languageParamRegex,
              })}>
                {isLangSwitcherFixOn && <AppDataContainer />}
                <ScrollToTop />
                <AccountResetPasswordConfirmPage />
              </Route>

              <Route exact path={intl.formatMessage(routes.accountOverviewPageRoute, {
                languageParamRegex,
              })}>
                {isLangSwitcherFixOn && <AppDataContainer />}
                <ScrollToTop />
                <PrerenderContainer storeData entity />
                <AccountRedirectContainer>
                  <AccountOverviewPageWithEntity/>
                </AccountRedirectContainer>
              </Route>

              <Route exact path={intl.formatMessage(routes.accountOrdersPageRoute, {
                languageParamRegex,
              })}>
                {isLangSwitcherFixOn && <AppDataContainer />}
                <ScrollToTop />
                <AccountRedirectContainer>
                  <AccountOrdersPageContainer />
                </AccountRedirectContainer>
              </Route>

              <Route exact path={intl.formatMessage(routes.accountAddressBookPageRoute, {
                languageParamRegex,
              })}>
                {isLangSwitcherFixOn && <AppDataContainer />}
                <ScrollToTop />
                <AccountRedirectContainer>
                  Account adresbook
                </AccountRedirectContainer>
              </Route>

              <Route exact path={intl.formatMessage(routes.accountDataPageRoute, {
                languageParamRegex,
              })}>
                {isLangSwitcherFixOn && <AppDataContainer />}
                <ScrollToTop />
                <GoogleReCaptchaProvider
                  reCaptchaKey={CONFIG.reCaptchaKey}
                  scriptProps={{ defer: true }}
                >
                  <AccountRedirectContainer>
                    <AccountDetailsPageContainer />
                  </AccountRedirectContainer>
                </GoogleReCaptchaProvider>
              </Route>
              <Route exact path={intl.formatMessage(routes.reorderPageRoute, {
                languageParamRegex,
              })}>
                {isLangSwitcherFixOn && <AppDataContainer />}
                <ScrollToTop />
                <PrerenderContainer storeData entity />
                <ReorderContainer />
              </Route>
              <Route exact path={intl.formatMessage(routes.notFoundPageRoute, {
                languageParamRegex,
              })}>
                {isLangSwitcherFixOn && <AppDataContainer />}
                <ScrollToTop />
                <NotFoundPage />
              </Route>
              <Route path={intl.formatMessage(routes.landingPageRoute, {
                languageParamRegex,
              })}>
                {isLangSwitcherFixOn && <AppDataContainer />}
                <ScrollToTop />
                <LandingPage />
              </Route>
              <Route path='*'>
                {isLangSwitcherFixOn && <AppDataContainer />}
                <ScrollToTop />
                <NotFoundPage />
              </Route>
            </Switch>
          </React.Suspense>
        </AppContainer>
      </Router>
    </>
  );
});
RoutingContainer.displayName = 'RoutingContainer';
